import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { listBadges, listUserBadges } from '../redux/slices/badges';
import { BadgeDefinition, UserBadgeView } from '../types/badges';

const computeProgressPct = (
	definition: BadgeDefinition,
	currentValue: number | undefined,
	unlocked: boolean
): number => {
	if (unlocked) return 100;
	// Easter-egg badges are all-or-nothing: there is no partial progress toward
	// solving the click sequence, so an unsolved one sits at 0.
	const threshold = definition.criteria.threshold;
	if (currentValue === undefined || threshold === undefined || threshold <= 0) return 0;
	const pct = (currentValue / threshold) * 100;
	return Math.max(0, Math.min(100, Math.round(pct)));
};

interface UseUserBadgesResult {
	badges: UserBadgeView[];
	unlockedCount: number;
	totalCount: number;
	isLoading: boolean;
}

interface UseUserBadgesOptions {
	/**
	 * When true, re-fetches the user's unlock state on every route change (and
	 * whenever the tab regains focus). Badges are only ever unlocked as a result
	 * of completing a task on another page — uploading a document, requesting a
	 * verification, etc. — so a fresh check on navigation surfaces the unlock the
	 * moment the user lands back on any page, without any periodic polling. Used
	 * by the global celebration watcher.
	 */
	refreshOnNavigate?: boolean;
}

/**
 * Loads the badge catalogue and the current user's unlock state / progress,
 * then merges them into ready-to-render {@link UserBadgeView} items.
 *
 * Secret badges that are not yet unlocked are hidden from the returned list.
 */
export const useUserBadges = (options: UseUserBadgesOptions = {}): UseUserBadgesResult => {
	const { refreshOnNavigate } = options;
	const dispatch = useAppDispatch();
	const location = useLocation();
	const catalogue = useAppSelector((state) => state.badges.catalogue);
	const userBadges = useAppSelector((state) => state.badges.userBadges);
	const userProgress = useAppSelector((state) => state.badges.userProgress);
	const catalogueLoading = useAppSelector(
		(state) => state.badges.catalogueRequest.isLoading
	);
	const userBadgesLoading = useAppSelector(
		(state) => state.badges.userBadgesRequest.isLoading
	);

	useEffect(() => {
		dispatch(listBadges());
		dispatch(listUserBadges());
	}, [dispatch]);

	// Event-driven refresh of unlock state for the celebration watcher. Register
	// the focus/visibility listeners once (they don't depend on the route), so we
	// don't tear down and re-add global listeners on every navigation.
	useEffect(() => {
		if (!refreshOnNavigate) {
			return undefined;
		}
		const refresh = () => {
			if (document.visibilityState === 'visible') {
				dispatch(listUserBadges());
			}
		};
		window.addEventListener('focus', refresh);
		document.addEventListener('visibilitychange', refresh);
		return () => {
			window.removeEventListener('focus', refresh);
			document.removeEventListener('visibilitychange', refresh);
		};
	}, [dispatch, refreshOnNavigate]);

	// Re-check unlock state whenever the user navigates to a new route (they just
	// finished a task). No timers — nothing fires while the user sits idle.
	useEffect(() => {
		if (!refreshOnNavigate) {
			return;
		}
		dispatch(listUserBadges());
	}, [dispatch, refreshOnNavigate, location.pathname]);

	const badges = useMemo<UserBadgeView[]>(() => {
		const unlockedMap = new Map(userBadges.map((b) => [b.badgeId, b]));
		const progressMap = new Map(userProgress.map((p) => [p.metric, p.value]));

		return catalogue
			.filter((definition) => definition.active)
			.map((definition) => {
				const unlock = unlockedMap.get(definition.id);
				const unlocked = Boolean(unlock?.unlocked);
				const currentValue = definition.criteria.metric
					? progressMap.get(definition.criteria.metric)
					: undefined;
				return {
					...definition,
					unlocked,
					unlockedAt: unlock?.unlockedAt,
					seenByUser: Boolean(unlock?.seenByUser),
					currentValue,
					progressPct: computeProgressPct(definition, currentValue, unlocked)
				};
			})
			.sort((a, b) => {
				if (a.unlocked !== b.unlocked) return a.unlocked ? -1 : 1;
				return b.progressPct - a.progressPct;
			});
	}, [catalogue, userBadges, userProgress]);

	const unlockedCount = badges.filter((b) => b.unlocked).length;

	return {
		badges,
		unlockedCount,
		totalCount: badges.length,
		isLoading: catalogueLoading || userBadgesLoading
	};
};
